import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { SsoButtons } from "@/components/parcelio/sso-buttons";
import { authApi, ApiError } from "@/lib/api";
import { useAuth } from "@/stores/auth";

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const setSession = useAuth((s) => s.setSession);

  useEffect(() => {
    const ssoError = searchParams.get("sso_error");
    if (ssoError) toast.error(ssoError);
  }, [searchParams]);

  return (
    <div>
      <h1 className="text-2xl font-bold tracking-tight">Welcome back</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Sign in to open your operator console.
      </p>

      <div className="mt-6">
        <SsoButtons mode="login" />
      </div>
      <div className="my-6 flex items-center gap-3">
        <div className="h-px flex-1 bg-border" />
        <span className="text-xs uppercase tracking-wider text-muted-foreground">or with email</span>
        <div className="h-px flex-1 bg-border" />
      </div>

      <form
        className="space-y-4"
        onSubmit={async (e) => {
          e.preventDefault();
          setLoading(true);
          const formData = new FormData(e.currentTarget);
          const email = formData.get("email") as string;
          const password = formData.get("password") as string;
          try {
            const session = await authApi.login(email, password);
            setSession(session);
            toast.success("Signed in — welcome back");
            navigate("/app/dashboard");
          } catch (err) {
            if (err instanceof ApiError && err.status === 403) {
              toast.error("Please confirm your email before signing in");
            } else {
              toast.error("Invalid email or password");
            }
          } finally {
            setLoading(false);
          }
        }}
      >
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" name="email" type="email" placeholder="you@company.com" required autoComplete="email" />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link to="/auth/reset-password" className="text-xs font-medium text-info hover:underline">
              Forgot password?
            </Link>
          </div>
          <Input id="password" name="password" type="password" placeholder="••••••••" required autoComplete="current-password" />
        </div>
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <Checkbox id="remember" />
          Keep me signed in on this device
        </label>
        <Button type="submit" className="w-full" disabled={loading}>
          {loading ? "Signing in…" : "Sign in"}
        </Button>
      </form>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        New to Parcelio?{" "}
        <Link to="/auth/register" className="font-medium text-foreground hover:underline">
          Create an account
        </Link>
      </p>
      <p className="mt-3 text-center text-xs text-muted-foreground">
        <Link to="/hackathon-nipost" className="font-medium text-info hover:underline">
          NIPOST Postcode Hackathon submission →
        </Link>
      </p>
    </div>
  );
}
